import {
  applyPriceShortcut,
  buildCollectionFilterUrl,
  buildLegacyFilterMigrationStagingUrl,
  changeCategory,
  clearFilterGroup,
  createEffectiveDirectFilterConfig,
  createEffectivePriceShortcuts,
  findMatchingPriceShortcut,
  isJpyFilterContext,
  isLegacyCanonicalFilterUrl,
  isLegacyFilterMigrationPending,
  isShoeSizeKey,
  localizeDirectFilterConfig,
  localizePriceShortcuts,
  normalizeFilterState,
  normalizeFilterDisplaySettings,
  normalizeNativeFilterInventory,
  normalizePricePointInventory,
  parseAppliedFilterState,
  removePriceFilterState,
  removeFilterValue,
  visibleFilterDisplayGroupKeys,
  visibleFilterDisplayOptionKeys,
} from "./cielohuk-direct-filter-core.js?v=20260823-1";
import { DIRECT_FILTER_CONFIG } from "./cielohuk-direct-filter-config.js?v=20260823-1";

const REMOVE_EVENT = "cielo-direct-filter:remove";
let instanceCount = 0;

function cloneState(state) {
  return {
    ...state,
    colors: [...(state.colors ?? [])],
    sizes: [...(state.sizes ?? [])],
  };
}

function emptyState() {
  return {
    category: null,
    subcategory: null,
    sleeve: null,
    genre: null,
    colors: [],
    sizes: [],
    priceMin: null,
    priceMax: null,
  };
}

function createElement(tagName, className, text) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

function parsePricePoints(element) {
  const source = element.querySelector("[data-cielo-price-points]");
  if (!source) return [];

  try {
    const parsed = JSON.parse(source.textContent || "[]");
    return normalizePricePointInventory(parsed);
  } catch {
    return [];
  }
}

function parseNativeFilters(element) {
  const source = element.querySelector("[data-cielo-native-filters]");
  if (!source) return [];

  try {
    return normalizeNativeFilterInventory(JSON.parse(source.textContent || "[]"));
  } catch {
    return [];
  }
}

function parseDisplaySettings(element) {
  const source = element.querySelector("[data-cielo-display-settings]");
  if (!source) return normalizeFilterDisplaySettings(null, DIRECT_FILTER_CONFIG);

  try {
    return normalizeFilterDisplaySettings(
      JSON.parse(source.textContent || "null"),
      DIRECT_FILTER_CONFIG,
    );
  } catch {
    return normalizeFilterDisplaySettings(null, DIRECT_FILTER_CONFIG);
  }
}

function parseTranslations(element) {
  const source =
    element?.querySelector?.("[data-cielo-filter-translations]") ??
    document.querySelector("[data-cielo-filter-translations]");
  if (!source) return {};

  try {
    const parsed = JSON.parse(source.textContent || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function readFilterContext(element) {
  const context = element?.closest?.("[data-cielo-filter-context]") ??
    element?.querySelector?.("[data-cielo-filter-context]") ??
    document.querySelector("[data-cielo-filter-context]");
  const data = context?.dataset ?? {};
  const country = String(data.country ?? "").trim().toUpperCase();
  const currency = String(data.currency ?? "").trim().toUpperCase();
  return {
    locale: data.locale || "ja",
    country,
    currency,
    currencySymbol: data.currencySymbol || "¥",
    priceFilterEnabled:
      data.priceFilterEnabled === "true" &&
      isJpyFilterContext({ country, currency }),
  };
}

function interpolate(template, values = {}) {
  return String(template).replace(/\{\{\s*([\w]+)\s*\}\}/gu, (_, key) =>
    values[key] === undefined ? "" : String(values[key]),
  );
}

function isEnglishLocale(locale) {
  return String(locale || "ja").toLowerCase().startsWith("en");
}

function isPrimaryJapaneseLocale(locale) {
  return String(locale || "ja").toLowerCase().startsWith("ja");
}

function translationText(translations, key, fallback, values = {}) {
  const candidate = translations?.ui?.[key];
  const template =
    typeof candidate === "string" &&
    candidate.trim() !== "" &&
    !candidate.trim().toLowerCase().startsWith("translation missing:")
      ? candidate
      : fallback;
  return interpolate(template, values);
}

function isStateEmpty(state) {
  return (
    !state.category &&
    !state.subcategory &&
    !state.sleeve &&
    !state.genre &&
    state.colors.length === 0 &&
    state.sizes.length === 0 &&
    state.priceMin === null &&
    state.priceMax === null
  );
}

function formatYen(value, locale = "ja") {
  return new Intl.NumberFormat(
    isEnglishLocale(locale) ? "en-US" : "ja-JP",
  ).format(value);
}

function priceLabel(state, translations = {}, context = {}) {
  if (state.priceMin === null && state.priceMax === null) return null;
  const locale = context.locale || "ja";
  const english = isEnglishLocale(locale);
  const amount = (value) => {
    const formatted = formatYen(value, locale);
    return english ? `${context.currencySymbol || "¥"}${formatted}` : formatted;
  };
  if (state.priceMin === null) {
    return translationText(
      translations,
      "price_below",
      english ? "{{ amount }} or less" : "{{ amount }}円以下",
      { amount: amount(state.priceMax) },
    );
  }
  if (state.priceMax === null) {
    return translationText(
      translations,
      "price_above",
      english ? "{{ amount }} or more" : "{{ amount }}円以上",
      { amount: amount(state.priceMin) },
    );
  }
  return translationText(
    translations,
    "price_between",
    english ? "{{ min }}–{{ max }}" : "{{ min }}〜{{ max }}円",
    { min: amount(state.priceMin), max: amount(state.priceMax) },
  );
}

class CielohukDirectFilter extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    this._instanceId = ++instanceCount;
    this._baseCollectionPath = this.dataset.baseCollectionUrl;
    this._context = readFilterContext(this);
    this._translations = parseTranslations(this);
    this._groups = this.querySelector("[data-cielo-filter-groups]");
    this._status = this.querySelector("[data-cielo-filter-status]");
    this._pricePoints = parsePricePoints(this);
    this._nativeFilters = parseNativeFilters(this);
    this._displaySettings = parseDisplaySettings(this);
    this._config = localizeDirectFilterConfig(
      createEffectiveDirectFilterConfig(
        DIRECT_FILTER_CONFIG,
        this._displaySettings,
      ),
      this._translations,
    );
    this._priceShortcuts = localizePriceShortcuts(
      createEffectivePriceShortcuts(this._displaySettings),
      this._translations,
    );
    if (!this._context.priceFilterEnabled) {
      const sanitizedUrl = removePriceFilterState(window.location.href);
      if (sanitizedUrl !== window.location.href) {
        window.location.replace(sanitizedUrl);
        return;
      }
    }
    this._applied = parseAppliedFilterState({
      baseCollectionPath: this._baseCollectionPath,
      currentUrl: window.location.href,
      config: this._config,
      priceFilterEnabled: this._context.priceFilterEnabled,
    });
    this._pending = cloneState(this._applied);

    const isLegacyCanonical = isLegacyCanonicalFilterUrl({
      baseCollectionPath: this._baseCollectionPath,
      currentUrl: window.location.href,
    });
    if (
      isLegacyCanonical ||
      isLegacyFilterMigrationPending(window.location.href)
    ) {
      const migrated = buildCollectionFilterUrl({
        baseCollectionPath: this._baseCollectionPath,
        currentUrl: window.location.href,
        pending: this._applied,
        config: this._config,
        pricePoints: this._pricePoints,
        nativeFilters: this._nativeFilters,
        priceFilterEnabled: this._context.priceFilterEnabled,
      });
      if (migrated.ok) {
        window.location.replace(migrated.url);
        return;
      }
      if (isLegacyCanonical) {
        window.location.replace(
          buildLegacyFilterMigrationStagingUrl({
            baseCollectionPath: this._baseCollectionPath,
            currentUrl: window.location.href,
            state: this._applied,
            config: this._config,
            priceFilterEnabled: this._context.priceFilterEnabled,
          }),
        );
        return;
      }
    }

    this._onClick = this._handleClick.bind(this);
    this._onChange = this._handleChange.bind(this);
    this._onRemove = this._handleRemove.bind(this);
    this._onClose = this._discardPending.bind(this);
    this._dialog = this.closest("dialog");
    this.addEventListener("click", this._onClick);
    this.addEventListener("change", this._onChange);
    document.addEventListener(REMOVE_EVENT, this._onRemove);
    this._dialog?.addEventListener("close", this._onClose);
    this.render();
  }

  disconnectedCallback() {
    if (!this._connected) return;
    this.removeEventListener("click", this._onClick);
    this.removeEventListener("change", this._onChange);
    document.removeEventListener(REMOVE_EVENT, this._onRemove);
    this._dialog?.removeEventListener("close", this._onClose);
    this._dialog = null;
    this._connected = false;
  }

  _discardPending() {
    this._pending = cloneState(this._applied);
    this.render();
  }

  _showStatus(message, kind = "error") {
    if (!this._status) return;
    this._status.hidden = !message;
    this._status.dataset.kind = kind;
    this._status.textContent = message || "";
  }

  _selectedValues(group) {
    const value = this._pending[group.stateKey];
    return group.multiple ? value : value ? [value] : [];
  }

  _visibleOptions(groupKey) {
    if (
      groupKey === "subcategory" &&
      !isPrimaryJapaneseLocale(this._context.locale)
    ) {
      return [];
    }
    const group = this._config.groups[groupKey];
    if (!group) return [];
    const optionByKey = new Map(group.values.map((option) => [option.key, option]));
    const allowedSubcategories =
      groupKey === "subcategory"
        ? this._config.subcategoriesByCategory[this._pending.category] ?? []
        : null;
    return visibleFilterDisplayOptionKeys(this._displaySettings, groupKey)
      .map((key) => optionByKey.get(key))
      .filter((option) => {
        if (!option) return false;
        if (allowedSubcategories && !allowedSubcategories.includes(option.key)) {
          return false;
        }
        return !(
          groupKey === "size" &&
          (isShoeSizeKey(option.key) || option.shoeOnly === true) &&
          this._pending.category !== "sho"
        );
      });
  }

  _optionCount(groupKey, option) {
    const group = this._config.groups[groupKey];
    const source = option.source ?? group?.source;
    if (source?.kind !== "native") return null;
    const filter = this._nativeFilters.find(
      (candidate) => candidate.paramName === source.paramName,
    );
    if (!filter) return null;
    const wanted = (option.queryValues ?? [option.label]).map((value) =>
      String(value).normalize("NFKC").toLocaleLowerCase("ja-JP"),
    );
    const matches = filter.values.filter((value) => {
      const candidates = [value.label, value.value].map((candidate) =>
        String(candidate).normalize("NFKC").toLocaleLowerCase("ja-JP"),
      );
      return source.matchMode === "contains"
        ? candidates.some((candidate) =>
            wanted.some((expected) => expected && candidate.includes(expected)),
          )
        : candidates.some((candidate) => wanted.includes(candidate));
    });
    if (matches.length === 0 || matches.some(({ count }) => count === null)) {
      return null;
    }
    return matches.reduce((total, { count }) => total + count, 0);
  }

  _renderTagGroup(groupKey) {
    const group = this._config.groups[groupKey];
    const selected = this._selectedValues(group);
    const details = createElement("details", "cielo-filter__group");
    details.open = true;
    details.dataset.group = groupKey;

    const summary = createElement("summary", "cielo-filter__summary");
    const title = createElement("span", "cielo-filter__title", group.title);
    summary.append(title);
    if (selected.length > 0) {
      summary.append(
        createElement("span", "cielo-filter__selected-count", String(selected.length)),
      );
    }
    summary.append(createElement("span", "cielo-filter__chevron", "⌄"));
    summary.setAttribute(
      "aria-label",
      selected.length > 0
        ? translationText(
            this._translations,
            "group_summary_count",
            isEnglishLocale(this._context.locale)
              ? "{{ label }} ({{ count }} selected)"
              : "{{ label }}（{{ count }}件選択中）",
            { label: group.title, count: selected.length },
          )
        : group.title,
    );
    details.append(summary);

    const body = createElement("div", "cielo-filter__body");
    if (selected.length > 0) {
      const clear = createElement(
        "button",
        "cielo-filter__group-clear",
        translationText(
          this._translations,
          "clear_group",
          isEnglishLocale(this._context.locale) ? "Clear" : "クリア",
        ),
      );
      clear.type = "button";
      clear.dataset.clearGroup = group.stateKey;
      clear.setAttribute(
        "aria-label",
        translationText(
          this._translations,
          "aria_clear_group",
          isEnglishLocale(this._context.locale)
            ? "Clear {{ label }} filters"
            : "{{ label }}の条件をクリア",
          { label: group.title },
        ),
      );
      body.append(clear);
    }

    const choices = createElement("div", "cielo-filter__choices");
    for (const option of this._visibleOptions(groupKey)) {
      const label = createElement("label", "cielo-filter__choice");
      const isColorChoice = groupKey === "color";
      if (isColorChoice) label.classList.add("cielo-filter__choice--color");
      const input = document.createElement("input");
      input.type = group.multiple ? "checkbox" : "radio";
      input.name = `cielo-filter-${this._instanceId}-${groupKey}`;
      input.value = option.key;
      input.checked = selected.includes(option.key);
      input.dataset.filterGroup = groupKey;
      input.dataset.filterKey = option.key;
      label.append(input);
      const mark = createElement(
        "span",
        `cielo-filter__choice-mark${isColorChoice ? " cielo-filter__color-swatch" : ""}`,
      );
      mark.setAttribute("aria-hidden", "true");
      if (isColorChoice) {
        mark.style.setProperty("--cielo-filter-swatch", option.swatch);
      }
      label.append(mark);
      const optionLabel = createElement(
        "span",
        "cielo-filter__choice-label",
        option.label,
      );
      const count = this._optionCount(groupKey, option);
      if (count !== null) {
        optionLabel.append(
          document.createTextNode(
            translationText(
              this._translations,
              "option_count",
              isEnglishLocale(this._context.locale)
                ? " ({{ count }})"
                : "（{{ count }}）",
              {
                count: new Intl.NumberFormat(
                  this._context.locale || "ja",
                ).format(count),
              },
            ),
          ),
        );
      }
      label.append(optionLabel);
      choices.append(label);
    }
    body.append(choices);
    details.append(body);
    return details;
  }

  _renderPriceGroup() {
    const details = createElement("details", "cielo-filter__group cielo-filter__group--price");
    details.open = true;
    details.dataset.group = "price";
    const hasPrice = this._pending.priceMin !== null || this._pending.priceMax !== null;

    const summary = createElement("summary", "cielo-filter__summary");
    const priceTitle = translationText(
      this._translations,
      "price_title",
      isEnglishLocale(this._context.locale) ? "Price" : "価格",
    );
    summary.append(createElement("span", "cielo-filter__title", priceTitle));
    if (hasPrice) {
      summary.append(createElement("span", "cielo-filter__selected-count", "1"));
    }
    summary.append(createElement("span", "cielo-filter__chevron", "⌄"));
    summary.setAttribute(
      "aria-label",
      hasPrice
        ? translationText(
            this._translations,
            "group_summary_count",
            isEnglishLocale(this._context.locale)
              ? "{{ label }} ({{ count }} selected)"
              : "{{ label }}（{{ count }}件選択中）",
            { label: priceTitle, count: 1 },
          )
        : priceTitle,
    );
    details.append(summary);

    const body = createElement("div", "cielo-filter__body");
    if (hasPrice) {
      const clear = createElement(
        "button",
        "cielo-filter__group-clear",
        translationText(
          this._translations,
          "clear_group",
          isEnglishLocale(this._context.locale) ? "Clear" : "クリア",
        ),
      );
      clear.type = "button";
      clear.dataset.clearGroup = "price";
      clear.setAttribute(
        "aria-label",
        translationText(
          this._translations,
          "aria_clear_group",
          isEnglishLocale(this._context.locale)
            ? "Clear {{ label }} filters"
            : "{{ label }}の条件をクリア",
          { label: priceTitle },
        ),
      );
      body.append(clear);
    }

    const inputs = createElement("div", "cielo-filter__price-inputs");
    for (const boundary of ["min", "max"]) {
      const label = createElement(
        "label",
        "cielo-filter__price-field",
      );
      label.append(
        createElement(
          "span",
          "cielo-filter__price-label",
          boundary === "min"
            ? translationText(
                this._translations,
                "minimum_price",
                isEnglishLocale(this._context.locale) ? "Minimum price" : "最低価格",
              )
            : translationText(
                this._translations,
                "maximum_price",
                isEnglishLocale(this._context.locale) ? "Maximum price" : "最高価格",
              ),
        ),
      );
      const inputWrap = createElement("span", "cielo-filter__price-control");
      inputWrap.append(
        createElement(
          "span",
          "cielo-filter__currency",
          this._context.currencySymbol || "¥",
        ),
      );
      const input = document.createElement("input");
      input.type = "number";
      input.inputMode = "numeric";
      input.min = "0";
      input.step = "1";
      input.placeholder =
        boundary === "min"
          ? translationText(
              this._translations,
              "no_minimum",
              isEnglishLocale(this._context.locale) ? "No minimum" : "下限なし",
            )
          : translationText(
              this._translations,
              "no_maximum",
              isEnglishLocale(this._context.locale) ? "No maximum" : "上限なし",
            );
      input.setAttribute(
        "aria-label",
        boundary === "min"
          ? translationText(
              this._translations,
              "minimum_price",
              isEnglishLocale(this._context.locale) ? "Minimum price" : "最低価格",
            )
          : translationText(
              this._translations,
              "maximum_price",
              isEnglishLocale(this._context.locale) ? "Maximum price" : "最高価格",
            ),
      );
      input.value = this._pending[boundary === "min" ? "priceMin" : "priceMax"] ?? "";
      input.dataset.priceBoundary = boundary;
      input.disabled = this._pricePoints.length === 0;
      inputWrap.append(input);
      label.append(inputWrap);
      inputs.append(label);
    }
    body.append(inputs);

    let matchingShortcut = null;
    try {
      matchingShortcut = findMatchingPriceShortcut(
        this._pending,
        this._priceShortcuts,
      );
    } catch {
      matchingShortcut = null;
    }
    const shortcuts = createElement("div", "cielo-filter__price-shortcuts");
    const shortcutById = new Map(
      this._priceShortcuts.map((shortcut) => [shortcut.id, shortcut]),
    );
    for (const shortcutId of visibleFilterDisplayOptionKeys(
      this._displaySettings,
      "price",
    )) {
      const shortcut = shortcutById.get(shortcutId);
      if (!shortcut) continue;
      const button = createElement("button", "cielo-filter__price-shortcut", shortcut.label);
      button.type = "button";
      button.dataset.priceShortcut = shortcut.id;
      button.disabled = this._pricePoints.length === 0;
      button.setAttribute("aria-pressed", String(matchingShortcut === shortcut.id));
      if (matchingShortcut === shortcut.id) button.classList.add("is-selected");
      shortcuts.append(button);
    }
    body.append(shortcuts);

    if (this._pricePoints.length === 0) {
      body.append(
        createElement(
          "p",
          "cielo-filter__notice",
          translationText(
            this._translations,
            "price_unavailable",
            isEnglishLocale(this._context.locale)
              ? "The price filter will be available after data synchronization."
              : "価格フィルターはデータ同期後に利用できます。",
          ),
        ),
      );
    }
    details.append(body);
    return details;
  }

  render() {
    if (!this._groups) return;
    const fragment = document.createDocumentFragment();
    for (const groupKey of visibleFilterDisplayGroupKeys(this._displaySettings)) {
      if (groupKey === "price" && !this._context.priceFilterEnabled) continue;
      if (
        groupKey === "subcategory" &&
        !isPrimaryJapaneseLocale(this._context.locale)
      ) continue;
      if (groupKey === "subcategory" && !this._pending.category) continue;
      if (groupKey !== "price" && this._visibleOptions(groupKey).length === 0) {
        continue;
      }
      fragment.append(
        groupKey === "price"
          ? this._renderPriceGroup()
          : this._renderTagGroup(groupKey),
      );
    }
    this._groups.replaceChildren(fragment);
    const clearAll = this.querySelector("[data-cielo-clear-all]");
    if (clearAll) clearAll.disabled = isStateEmpty(this._pending);
  }

  _handleChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    if (input.dataset.priceBoundary) {
      const stateKey = input.dataset.priceBoundary === "min" ? "priceMin" : "priceMax";
      this._pending[stateKey] = input.value === "" ? null : Number(input.value);
      this._showStatus(null);
      this.render();
      return;
    }

    const groupKey = input.dataset.filterGroup;
    const optionKey = input.dataset.filterKey;
    const group = this._config.groups[groupKey];
    if (!group || !optionKey) return;

    if (group.multiple) {
      const selected = new Set(this._pending[group.stateKey]);
      input.checked ? selected.add(optionKey) : selected.delete(optionKey);
      this._pending[group.stateKey] = group.values
        .filter((option) => selected.has(option.key))
        .map((option) => option.key);
    } else if (groupKey === "category") {
      this._pending = changeCategory(
        this._pending,
        optionKey,
        this._config,
      );
    } else {
      this._pending[group.stateKey] = optionKey;
    }
    this._showStatus(null);
    this.render();
  }

  _clearGroup(stateKey) {
    this._pending = clearFilterGroup(
      this._pending,
      stateKey,
      this._config,
    );
  }

  _handleClick(event) {
    const target = event.target.closest("button");
    if (!target || !this.contains(target)) return;

    // Kalles' drawer also observes bubbled button clicks. Keep every filter
    // action inside this component so price shortcuts and clear buttons do not
    // close the drawer and discard the pending selections before Apply.
    event.preventDefault();
    event.stopPropagation();

    if (target.dataset.priceShortcut) {
      this._pending = applyPriceShortcut(
        this._pending,
        target.dataset.priceShortcut,
        this._priceShortcuts,
      );
      this._showStatus(null);
      this.render();
      return;
    }
    if (target.dataset.clearGroup) {
      this._clearGroup(target.dataset.clearGroup);
      this._showStatus(null);
      this.render();
      return;
    }
    if (target.hasAttribute("data-cielo-clear-all")) {
      this._pending = emptyState();
      this._showStatus(null);
      this.render();
      return;
    }
    if (!target.hasAttribute("data-cielo-apply")) return;

    try {
      const result = buildCollectionFilterUrl({
        baseCollectionPath: this._baseCollectionPath,
        currentUrl: window.location.href,
        pending: this._pending,
        config: this._config,
        pricePoints: this._pricePoints,
        nativeFilters: this._nativeFilters,
        priceFilterEnabled: this._context.priceFilterEnabled,
      });
      if (!result.ok) {
        this._showStatus(
          result.error === "NATIVE_FILTER_UNAVAILABLE"
            ? translationText(
                this._translations,
                "native_filter_unavailable",
                isEnglishLocale(this._context.locale)
                  ? "Please check the standard product filter settings and try again in a moment."
                  : "商品検索の標準フィルター設定を確認しています。少し待ってから再度お試しください。",
              )
            : result.error === "NO_PRICE_MATCH"
              ? translationText(
                  this._translations,
                  "no_price_match",
                  isEnglishLocale(this._context.locale)
                    ? "No products match this price range."
                    : "この価格帯の商品はありません。",
                )
              : translationText(
                  this._translations,
                  "no_filter_match",
                  isEnglishLocale(this._context.locale)
                    ? "No products match the selected filters."
                    : "選択した条件に該当する商品はありません。",
                ),
        );
        return;
      }
      window.location.assign(result.url);
    } catch (error) {
      this._showStatus(
        error instanceof RangeError
          ? translationText(
              this._translations,
              "invalid_price_range",
              isEnglishLocale(this._context.locale)
                ? "Minimum price must not exceed maximum price."
                : "最低価格は最高価格以下にしてください。",
            )
          : translationText(
              this._translations,
              "invalid_price_value",
              isEnglishLocale(this._context.locale)
                ? "Enter a whole number of 0 or more for the price."
                : "価格は0以上の整数で入力してください。",
            ),
      );
    }
  }

  _handleRemove(event) {
    const detail = event.detail;
    if (!detail || detail.baseCollectionPath !== this._baseCollectionPath) return;
    this._pending = removeFilterValue(
      this._applied,
      detail.stateKey,
      detail.key,
      this._config,
    );

    try {
      const result = buildCollectionFilterUrl({
        baseCollectionPath: this._baseCollectionPath,
        currentUrl: window.location.href,
        pending: this._pending,
        config: this._config,
        pricePoints: this._pricePoints,
        nativeFilters: this._nativeFilters,
        priceFilterEnabled: this._context.priceFilterEnabled,
      });
      if (!result.ok) {
        this._showStatus(
          result.error === "NATIVE_FILTER_UNAVAILABLE"
            ? translationText(
                this._translations,
                "native_filter_unavailable",
                isEnglishLocale(this._context.locale)
                  ? "Please check the standard product filter settings and try again in a moment."
                  : "商品検索の標準フィルター設定を確認しています。少し待ってから再度お試しください。",
              )
            : result.error === "NO_PRICE_MATCH"
              ? translationText(
                  this._translations,
                  "no_price_match",
                  isEnglishLocale(this._context.locale)
                    ? "No products match this price range."
                    : "この価格帯の商品はありません。",
                )
              : translationText(
                  this._translations,
                  "no_filter_match",
                  isEnglishLocale(this._context.locale)
                    ? "No products match the selected filters."
                    : "選択した条件に該当する商品はありません。",
                ),
        );
        this.render();
        return;
      }
      window.location.assign(result.url);
    } catch (error) {
      this._showStatus(
        error instanceof RangeError
          ? translationText(
              this._translations,
              "invalid_price_range",
              isEnglishLocale(this._context.locale)
                ? "Minimum price must not exceed maximum price."
                : "最低価格は最高価格以下にしてください。",
            )
          : translationText(
              this._translations,
              "invalid_price_value",
              isEnglishLocale(this._context.locale)
                ? "Enter a whole number of 0 or more for the price."
                : "価格は0以上の整数で入力してください。",
            ),
      );
      this.render();
    }
  }
}

class CielohukDirectFilterChips extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    this._context = readFilterContext(this);
    this._translations = parseTranslations(this);
    this._onClick = this._handleClick.bind(this);
    this.addEventListener("click", this._onClick);
    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this._onClick);
    this._connected = false;
  }

  _chip(label, stateKey, key = "") {
    const button = createElement("button", "cielo-filter-chips__chip");
    button.type = "button";
    button.dataset.stateKey = stateKey;
    button.dataset.key = key;
    button.setAttribute(
      "aria-label",
      translationText(
        this._translations,
        "aria_remove_filter",
        isEnglishLocale(this._context.locale)
          ? "Remove {{ label }} filter"
          : "{{ label }}を解除する",
        { label },
      ),
    );
    button.append(createElement("span", "", label));
    button.append(createElement("span", "cielo-filter-chips__remove", "×"));
    return button;
  }

  render() {
    const baseCollectionPath = this.dataset.baseCollectionUrl;
    const displaySettings = parseDisplaySettings(document);
    const config = localizeDirectFilterConfig(
      createEffectiveDirectFilterConfig(DIRECT_FILTER_CONFIG, displaySettings),
      this._translations,
    );
    const state = parseAppliedFilterState({
      baseCollectionPath,
      currentUrl: window.location.href,
      config,
      priceFilterEnabled: this._context.priceFilterEnabled,
    });
    const chips = [];
    for (const groupKey of ["category", "subcategory", "sleeve", "genre", "color", "size"]) {
      if (
        groupKey === "subcategory" &&
        !isPrimaryJapaneseLocale(this._context.locale)
      ) continue;
      const group = config.groups[groupKey];
      const values = group.multiple
        ? state[group.stateKey]
        : state[group.stateKey]
          ? [state[group.stateKey]]
          : [];
      for (const key of values) {
        const label = group.values.find((item) => item.key === key)?.label;
        if (label) chips.push(this._chip(label, group.stateKey, key));
      }
    }
    const currentPriceLabel = priceLabel(
      state,
      this._translations,
      this._context,
    );
    if (currentPriceLabel) chips.push(this._chip(currentPriceLabel, "price"));

    if (chips.length === 0) {
      this.hidden = true;
      this.replaceChildren();
      return;
    }
    this.hidden = false;
    const wrapper = createElement("div", "cielo-filter-chips__inner");
    wrapper.append(
      createElement(
        "span",
        "cielo-filter-chips__label",
        translationText(
          this._translations,
          "selected",
          isEnglishLocale(this._context.locale) ? "Selected" : "選択中",
        ),
      ),
    );
    wrapper.append(...chips);
    this.replaceChildren(wrapper);
  }

  _handleClick(event) {
    const button = event.target.closest("[data-state-key]");
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    event.stopPropagation();
    document.dispatchEvent(
      new CustomEvent(REMOVE_EVENT, {
        detail: {
          baseCollectionPath: this.dataset.baseCollectionUrl,
          stateKey: button.dataset.stateKey,
          key: button.dataset.key,
        },
      }),
    );
  }
}

if (!customElements.get("cielohuk-direct-filter")) {
  customElements.define("cielohuk-direct-filter", CielohukDirectFilter);
}
if (!customElements.get("cielohuk-direct-filter-chips")) {
  customElements.define("cielohuk-direct-filter-chips", CielohukDirectFilterChips);
}
